<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watchEffect } from "vue";
import { AlertCircle, AudioLines, BookOpenText, CheckCircle2, CircleStop, Link, Link2Off, LoaderCircle, Mic, Send, UserRound, Volume2, Wrench } from "lucide-vue-next";
import { api, ApiClientError } from "../services/api";
import { PcmPlayer, PcmRecorder } from "../services/audio";
import { decodeRealtimeFrame, encodeRealtimeEvent, RealtimeEvent } from "../services/realtime-protocol";
import type { AgentSummary, RealtimeTicket } from "../types";

type SessionState = "idle" | "requesting_permission" | "connecting" | "ready" | "recording" | "closing";
type DeviceControlState = "idle" | "checking" | "completed" | "failed";
type Transcript = { id: number; kind: "user" | "assistant" | "system"; text: string; interim?: boolean };

const props = defineProps<{ agents: AgentSummary[] }>();
const selectedAgent = ref("");
const state = ref<SessionState>("idle");
const autoConversation = ref(false);
const voiceDeviceControl = ref(true);
const voiceKnowledge = ref(true);
const deviceControlState = ref<DeviceControlState>("idle");
const knowledgeState = ref<DeviceControlState>("idle");
const deviceControlBusy = ref(false);
const silenceSeconds = ref(1.5);
const error = ref("");
const textInput = ref("");
const transcripts = ref<Transcript[]>([]);
const sessionId = ref("");
const ticketInfo = ref<RealtimeTicket>();
const recorder = new PcmRecorder();
const player = new PcmPlayer();
let socket: WebSocket | undefined;
let connectionTimer: number | undefined;
let sequence = 0;
let assistantEntry: Transcript | undefined;
let discardInterruptedAudio = false;
let finalAsrText = "";
let dialogTurnActive = false;
let pendingDeviceAnnouncement = "";
let pendingAnnouncementTraceId = "";
let suppressKnowledgeProviderReply = false;
let suppressedProviderChatEnded = false;
let suppressedProviderTtsEnded = false;
let lastControlText = "";
let lastControlAt = 0;
let controlGeneration = 0;

const realtimeAgents = computed(() => props.agents.filter((agent) => agent.capabilities.realtime));
const selectedAgentInfo = computed(() => realtimeAgents.value.find((agent) => agent.slug === selectedAgent.value) ?? realtimeAgents.value[0]);
const activeAgent = computed(() => selectedAgentInfo.value?.slug ?? "");
const deviceControlAvailable = computed(() => Boolean(selectedAgentInfo.value?.capabilities.deviceControl));
const knowledgeAvailable = computed(() => Boolean(selectedAgentInfo.value?.capabilities.knowledge));
const connected = computed(() => state.value === "ready" || state.value === "recording");
const silenceWindowMs = computed(() => Math.round(Math.min(5, Math.max(0.5, silenceSeconds.value)) * 1000));
const deviceControlLabel = computed(() => {
  if (!deviceControlAvailable.value) return "智能体未启用";
  if (!voiceDeviceControl.value) return "已关闭";
  if (deviceControlState.value === "checking") return "正在执行";
  if (deviceControlState.value === "completed") return "执行完成";
  if (deviceControlState.value === "failed") return "执行失败";
  return "已启用";
});
const knowledgeLabel = computed(() => {
  if (!knowledgeAvailable.value) return "智能体未启用";
  if (!voiceKnowledge.value) return "已关闭";
  if (knowledgeState.value === "checking") return "正在检索";
  if (knowledgeState.value === "completed") return "检索完成";
  if (knowledgeState.value === "failed") return "检索失败";
  return "已启用";
});

watchEffect(() => {
  if (!selectedAgent.value && realtimeAgents.value[0]) selectedAgent.value = realtimeAgents.value[0].slug;
});

function addSystem(text: string): void {
  transcripts.value.push({ id: ++sequence, kind: "system", text });
}

function logRealtimeFlow(stage: string, details: Record<string, unknown> = {}): void {
  console.info("[realtime-flow]", {
    stage,
    at: new Date().toISOString(),
    sessionId: sessionId.value || undefined,
    agent: activeAgent.value || undefined,
    ...details,
  });
}

function sendEvent(event: number, payload: Record<string, unknown> | Uint8Array, withSession = true): void {
  if (socket?.readyState !== WebSocket.OPEN) return;
  socket.send(encodeRealtimeEvent(event, payload, withSession ? sessionId.value : undefined));
}

function clearConnectionTimer(): void {
  if (connectionTimer !== undefined) window.clearTimeout(connectionTimer);
  connectionTimer = undefined;
}

function microphoneError(reason: unknown): string {
  if (!(reason instanceof DOMException)) return reason instanceof Error ? reason.message : "无法使用麦克风";
  if (reason.name === "NotAllowedError" || reason.name === "SecurityError") {
    return "麦克风权限未获允许，请在浏览器的网站权限中启用麦克风";
  }
  if (reason.name === "NotFoundError") return "未检测到可用麦克风，请检查设备连接";
  if (reason.name === "NotReadableError" || reason.name === "AbortError") return "麦克风暂时无法使用，可能正被其他程序占用";
  return reason.message || "无法使用麦克风";
}

function flushDeviceAnnouncement(): void {
  if (!pendingDeviceAnnouncement || !connected.value || dialogTurnActive || suppressKnowledgeProviderReply) return;
  const result = pendingDeviceAnnouncement;
  const traceId = pendingAnnouncementTraceId;
  pendingDeviceAnnouncement = "";
  pendingAnnouncementTraceId = "";
  dialogTurnActive = true;
  logRealtimeFlow("announcement_sent_to_provider", { traceId, text: result });
  sendEvent(RealtimeEvent.ChatTextQuery, {
    content: `【后台处理结果】${result}。请直接向用户播报这个最终结果，不要再次请求设备控制或知识库检索。`,
  });
}

function beginKnowledgeProviderSuppression(): void {
  if (!(voiceKnowledge.value && knowledgeAvailable.value) || suppressKnowledgeProviderReply) return;
  suppressKnowledgeProviderReply = true;
  suppressedProviderChatEnded = false;
  suppressedProviderTtsEnded = false;
}

function finishKnowledgeProviderSuppression(): void {
  if (!suppressKnowledgeProviderReply || !suppressedProviderChatEnded || !suppressedProviderTtsEnded) return;
  suppressKnowledgeProviderReply = false;
  logRealtimeFlow("provider_original_turn_suppressed", { pendingTraceId: pendingAnnouncementTraceId || undefined });
  flushDeviceAnnouncement();
}

function queueDeviceAnnouncement(text: string, traceId = ""): void {
  pendingDeviceAnnouncement = text;
  pendingAnnouncementTraceId = traceId;
  logRealtimeFlow("announcement_queued", {
    traceId: traceId || undefined,
    waitingForProviderTurn: dialogTurnActive || suppressKnowledgeProviderReply,
    text,
  });
  flushDeviceAnnouncement();
}

function normalizeUtterance(text: string): string {
  return text.normalize("NFKC").toLocaleLowerCase().replace(/[^\p{L}\p{N}]+/gu, "");
}

function isRepeatedUtterance(current: string, previous: string): boolean {
  if (!current || !previous) return false;
  if (current === previous) return true;
  return Math.min(current.length, previous.length) >= 6 && (current.includes(previous) || previous.includes(current));
}

async function handleRealtimeAugmentation(text: string): Promise<void> {
  const command = text.trim();
  const commandKey = normalizeUtterance(command);
  const allowDeviceControl = voiceDeviceControl.value && deviceControlAvailable.value;
  const allowKnowledge = voiceKnowledge.value && knowledgeAvailable.value;
  if (!command || (!allowDeviceControl && !allowKnowledge)) return;
  const now = Date.now();
  if (now - lastControlAt < 5000 && isRepeatedUtterance(commandKey, lastControlText)) return;
  if (deviceControlBusy.value) {
    addSystem("后台仍在处理上一句话");
    return;
  }

  const generation = controlGeneration;
  lastControlText = commandKey;
  lastControlAt = now;
  deviceControlBusy.value = true;
  logRealtimeFlow("backend_turn_started", { text: command, allowDeviceControl, allowKnowledge });
  if (allowDeviceControl) deviceControlState.value = "checking";
  if (allowKnowledge) knowledgeState.value = "checking";
  try {
    const result = await api.realtimeTurn(activeAgent.value, command, { allowDeviceControl, allowKnowledge });
    if (generation !== controlGeneration) return;
    logRealtimeFlow("backend_turn_completed", {
      traceId: result.traceId,
      kind: result.kind,
      handled: result.handled,
      answerMode: result.answerMode,
      text: result.text,
    });
    if (!result.handled) {
      deviceControlState.value = "idle";
      knowledgeState.value = "idle";
      return;
    }
    const message = result.text.trim() || (result.kind === "device" ? "设备命令已执行" : "请求已处理");
    if (result.kind === "device") {
      deviceControlState.value = "completed";
      knowledgeState.value = "idle";
      addSystem(`设备控制：${message}（追踪号：${result.traceId}）`);
    } else if (result.kind === "general") {
      deviceControlState.value = "idle";
      knowledgeState.value = "completed";
      addSystem(`智能体通用回答：${message}`);
    } else {
      deviceControlState.value = "idle";
      knowledgeState.value = "completed";
      const knowledgeSource = result.fallbackReason === "AI_TIMEOUT"
        ? "知识库（生成超时，已返回命中资料）"
        : result.fallbackReason === "REALTIME_FAST_PATH" ? "知识库（快速回答）" : "知识库";
      addSystem(`${knowledgeSource}：${message}`);
    }
    queueDeviceAnnouncement(message, result.traceId);
  } catch (reason) {
    if (generation !== controlGeneration) return;
    const message = reason instanceof Error ? reason.message : "后台扩展处理失败";
    const trace = reason instanceof ApiClientError && reason.traceId ? `（追踪号：${reason.traceId}）` : "";
    logRealtimeFlow("backend_turn_failed", {
      traceId: reason instanceof ApiClientError ? reason.traceId : undefined,
      message,
    });
    if (allowDeviceControl) deviceControlState.value = "failed";
    if (allowKnowledge) knowledgeState.value = "failed";
    addSystem(`后台扩展处理失败：${message}${trace}`);
    queueDeviceAnnouncement(`请求未完成，${message}`, reason instanceof ApiClientError ? reason.traceId || "" : "");
  } finally {
    if (generation === controlGeneration) deviceControlBusy.value = false;
  }
}

async function connect(): Promise<void> {
  if (!activeAgent.value || state.value !== "idle") return;
  state.value = "requesting_permission";
  error.value = "";
  transcripts.value = [];
  controlGeneration += 1;
  deviceControlBusy.value = false;
  deviceControlState.value = "idle";
  knowledgeState.value = "idle";
  finalAsrText = "";
  dialogTurnActive = false;
  pendingDeviceAnnouncement = "";
  pendingAnnouncementTraceId = "";
  suppressKnowledgeProviderReply = false;
  suppressedProviderChatEnded = false;
  suppressedProviderTtsEnded = false;
  lastControlText = "";
  lastControlAt = 0;
  try {
    await recorder.prepare();
    state.value = "connecting";
    const ticket = await api.realtimeTicket(activeAgent.value);
    ticketInfo.value = ticket;
    sessionId.value = crypto.randomUUID();
    socket = new WebSocket(ticket.url);
    socket.binaryType = "arraybuffer";
    connectionTimer = window.setTimeout(() => {
      if (state.value !== "connecting") return;
      error.value = "实时语音会话启动超时，请重试";
      void recorder.stop();
      socket?.close(1011, "Session startup timeout");
    }, 12_000);
    socket.onopen = () => sendEvent(RealtimeEvent.StartConnection, {}, false);
    socket.onmessage = (message) => void handleFrame(message.data as ArrayBuffer).catch((reason: unknown) => {
      clearConnectionTimer();
      error.value = reason instanceof Error ? `实时语音协议解析失败：${reason.message}` : "实时语音协议解析失败";
      void disconnect();
    });
    socket.onerror = () => {
      clearConnectionTimer();
      error.value = "实时语音连接失败";
      state.value = "idle";
    };
    socket.onclose = () => {
      clearConnectionTimer();
      void recorder.stop();
      player.stop();
      controlGeneration += 1;
      deviceControlBusy.value = false;
      deviceControlState.value = "idle";
      knowledgeState.value = "idle";
      finalAsrText = "";
      dialogTurnActive = false;
      pendingDeviceAnnouncement = "";
      pendingAnnouncementTraceId = "";
      suppressKnowledgeProviderReply = false;
      suppressedProviderChatEnded = false;
      suppressedProviderTtsEnded = false;
      if (state.value !== "idle") addSystem("会话已结束");
      state.value = "idle";
    };
  } catch (reason) {
    await recorder.stop();
    error.value = state.value === "requesting_permission"
      ? microphoneError(reason)
      : reason instanceof Error ? reason.message : "会话创建失败";
    state.value = "idle";
  }
}

async function handleFrame(buffer: ArrayBuffer): Promise<void> {
  const frame = decodeRealtimeFrame(buffer);
  if (frame.code) {
    error.value = `${frame.code}: ${String(frame.json?.error ?? "服务端错误")}`;
    return;
  }
  switch (frame.event) {
    case RealtimeEvent.ConnectionStarted: {
      const session = ticketInfo.value!.sessionConfig;
      const asrExtra: Record<string, unknown> = {
        enable_custom_vad: autoConversation.value,
        enable_asr_twopass: true,
        ...(autoConversation.value ? { end_smooth_window_ms: silenceWindowMs.value } : {}),
      };
      const dialogExtra: Record<string, unknown> = {
        ...(autoConversation.value ? {} : { input_mod: "push_to_talk" }),
        ...(session.model ? { model: session.model } : {}),
      };
      const extensionRules = [
        ...(voiceDeviceControl.value && deviceControlAvailable.value ? ["设备控制由后台工具并行处理。用户明确要求控制设备时，不要声称无法控制，只需简短告知正在处理。"] : []),
        ...(voiceKnowledge.value && knowledgeAvailable.value ? ["用户直接说话或发送的普通文本只用于识别，所有问题均由后台统一路由。在收到以【后台处理结果】开头的文本前必须保持静音，不要自行回答、解释、拒绝，也不要生成语音。收到后台处理结果后再自然播报其中内容。"] : []),
      ];
      const systemRole = extensionRules.length
        ? `${session.systemRole}\n\n${extensionRules.join("\n")}当收到以【后台处理结果】开头的文本时，只播报其中的最终结果，不要再次执行或改写。`
        : session.systemRole;
      sendEvent(RealtimeEvent.StartSession, {
        asr: {
          audio_info: { format: "pcm", sample_rate: 16000, channel: 1 },
          extra: asrExtra,
        },
        tts: {
          speaker: session.speaker,
          audio_config: { channel: 1, format: "pcm_s16le", sample_rate: 24000 },
          extra: {},
        },
        dialog: {
          bot_name: session.botName,
          system_role: systemRole,
          speaking_style: session.speakingStyle ?? "自然、清晰、简洁",
          extra: dialogExtra,
        },
      });
      break;
    }
    case RealtimeEvent.SessionStarted:
      clearConnectionTimer();
      state.value = "ready";
      addSystem("实时会话已建立");
      if (autoConversation.value) await startRecording();
      break;
    case RealtimeEvent.SessionFailed:
    case RealtimeEvent.ConnectionFailed:
      clearConnectionTimer();
      error.value = String(frame.json?.error ?? "实时会话启动失败");
      await disconnect();
      break;
    case RealtimeEvent.ASRInfo:
      player.stop();
      discardInterruptedAudio = true;
      assistantEntry = undefined;
      beginKnowledgeProviderSuppression();
      break;
    case RealtimeEvent.ASRResponse: {
      const results = frame.json?.results as Array<{ text?: string; is_interim?: boolean }> | undefined;
      const result = results?.[0];
      if (!result?.text) break;
      const isInterim = result.is_interim ?? false;
      const last = [...transcripts.value].reverse().find((item) => item.kind === "user" && item.interim);
      if (last) {
        last.text = result.text;
        last.interim = isInterim;
      } else {
        transcripts.value.push({ id: ++sequence, kind: "user", text: result.text, interim: isInterim });
      }
      if (!isInterim) finalAsrText = result.text.trim();
      break;
    }
    case RealtimeEvent.ASREnded: {
      discardInterruptedAudio = false;
      dialogTurnActive = true;
      const utterance = finalAsrText;
      finalAsrText = "";
      if (utterance) {
        beginKnowledgeProviderSuppression();
        void handleRealtimeAugmentation(utterance);
      }
      break;
    }
    case RealtimeEvent.ChatResponse: {
      const content = String(frame.json?.content ?? "");
      if (!content || suppressKnowledgeProviderReply) break;
      dialogTurnActive = true;
      if (!assistantEntry) {
        assistantEntry = { id: ++sequence, kind: "assistant", text: "" };
        transcripts.value.push(assistantEntry);
      }
      assistantEntry.text += content;
      break;
    }
    case RealtimeEvent.ChatEnded:
      assistantEntry = undefined;
      dialogTurnActive = false;
      logRealtimeFlow("provider_chat_ended", { suppressed: suppressKnowledgeProviderReply });
      if (suppressKnowledgeProviderReply) {
        suppressedProviderChatEnded = true;
        finishKnowledgeProviderSuppression();
      } else {
        flushDeviceAnnouncement();
      }
      break;
    case RealtimeEvent.TTSResponse:
      if (!discardInterruptedAudio && !suppressKnowledgeProviderReply) {
        await player.enqueue(frame.payload, ticketInfo.value?.sessionConfig.outputAudio.sampleRate ?? 24000);
      }
      break;
    case RealtimeEvent.TTSEnded:
      logRealtimeFlow("provider_tts_ended", { suppressed: suppressKnowledgeProviderReply });
      if (suppressKnowledgeProviderReply) {
        suppressedProviderTtsEnded = true;
        finishKnowledgeProviderSuppression();
      }
      break;
    case RealtimeEvent.SessionFinished:
      sendEvent(RealtimeEvent.FinishConnection, {}, false);
      window.setTimeout(() => socket?.close(1000), 300);
      break;
  }
}

async function startRecording(): Promise<void> {
  if (state.value !== "ready") return;
  error.value = "";
  try {
    await recorder.start((frame) => sendEvent(RealtimeEvent.TaskRequest, frame));
    state.value = "recording";
  } catch (reason) {
    error.value = reason instanceof Error ? reason.message : "无法使用麦克风";
  }
}

async function stopRecording(): Promise<void> {
  if (state.value !== "recording" || autoConversation.value) return;
  await recorder.stop();
  sendEvent(RealtimeEvent.EndASR, {});
  state.value = "ready";
}

function sendText(): void {
  const value = textInput.value.trim();
  if (!value || !connected.value) return;
  transcripts.value.push({ id: ++sequence, kind: "user", text: value });
  dialogTurnActive = true;
  beginKnowledgeProviderSuppression();
  sendEvent(RealtimeEvent.ChatTextQuery, { content: value });
  void handleRealtimeAugmentation(value);
  textInput.value = "";
}

async function disconnect(): Promise<void> {
  if (!socket) return;
  await recorder.stop();
  discardInterruptedAudio = false;
  state.value = "closing";
  sendEvent(RealtimeEvent.FinishSession, {});
  window.setTimeout(() => {
    if (socket?.readyState === WebSocket.OPEN) socket.close(1000);
  }, 1500);
}

onBeforeUnmount(async () => {
  clearConnectionTimer();
  controlGeneration += 1;
  socket?.close(1000);
  await recorder.stop();
  await player.close();
});
</script>

<template>
  <div class="realtime-layout">
    <section class="surface-section realtime-control">
      <div class="section-header">
        <div><span class="eyebrow">Realtime API</span><h2>语音会话</h2></div>
        <span :class="['connection-state', state]">{{ state === "idle" ? "未连接" : state === "requesting_permission" ? "申请权限" : state === "connecting" ? "连接中" : state === "recording" ? "收音中" : state === "closing" ? "关闭中" : "已就绪" }}</span>
      </div>
      <label class="form-field">
        <span>智能体</span>
        <select v-model="selectedAgent" :disabled="state !== 'idle'">
          <option value="" disabled>{{ realtimeAgents.length ? "请选择" : "没有启用实时语音的智能体" }}</option>
          <option v-for="agent in realtimeAgents" :key="agent.slug" :value="agent.slug">{{ agent.name }}</option>
        </select>
      </label>

      <div class="realtime-mode-panel">
        <div class="realtime-mode-row">
          <strong>自动对话</strong>
          <label class="toggle-control compact-toggle">
            <input v-model="autoConversation" type="checkbox" :disabled="state !== 'idle'" />
            <span class="toggle-track" />
          </label>
        </div>
        <div class="realtime-mode-row voice-control-row">
          <div class="realtime-mode-label"><Wrench :size="15" /><strong>语音设备控制</strong></div>
          <span :class="['voice-control-state', deviceControlState]">
            <LoaderCircle v-if="deviceControlState === 'checking'" :size="13" class="spinning" />
            <CheckCircle2 v-else-if="deviceControlState === 'completed'" :size="13" />
            <AlertCircle v-else-if="deviceControlState === 'failed'" :size="13" />
            {{ deviceControlLabel }}
          </span>
          <label class="toggle-control compact-toggle">
            <input v-model="voiceDeviceControl" type="checkbox" :disabled="state !== 'idle' || !deviceControlAvailable" />
            <span class="toggle-track" />
          </label>
        </div>
        <div class="realtime-mode-row voice-control-row">
          <div class="realtime-mode-label"><BookOpenText :size="15" /><strong>知识库问答</strong></div>
          <span :class="['voice-control-state', knowledgeState]">
            <LoaderCircle v-if="knowledgeState === 'checking'" :size="13" class="spinning" />
            <CheckCircle2 v-else-if="knowledgeState === 'completed'" :size="13" />
            <AlertCircle v-else-if="knowledgeState === 'failed'" :size="13" />
            {{ knowledgeLabel }}
          </span>
          <label class="toggle-control compact-toggle">
            <input v-model="voiceKnowledge" type="checkbox" :disabled="state !== 'idle' || !knowledgeAvailable" />
            <span class="toggle-track" />
          </label>
        </div>
        <label class="silence-control" :class="{ disabled: !autoConversation }">
          <span>静音后回答</span>
          <input v-model.number="silenceSeconds" type="range" min="0.5" max="5" step="0.1" :disabled="state !== 'idle' || !autoConversation" />
          <output>{{ silenceSeconds.toFixed(1) }} 秒</output>
        </label>
      </div>

      <div class="voice-stage">
        <div class="level-bars" :class="{ active: state === 'recording' }">
          <span v-for="index in 11" :key="index" :style="{ animationDelay: `${index * 45}ms` }" />
        </div>
        <button v-if="state === 'idle'" class="voice-button connect" type="button" title="建立语音会话" :disabled="!activeAgent" @click="connect"><Link :size="25" /></button>
        <button v-else-if="state === 'ready'" class="voice-button" type="button" title="开始说话" @click="startRecording"><Mic :size="27" /></button>
        <div v-else-if="state === 'recording' && autoConversation" class="voice-button recording" role="status" aria-label="自动聆听中"><Mic :size="27" /></div>
        <button v-else-if="state === 'recording'" class="voice-button recording" type="button" title="结束聆听" @click="stopRecording"><CircleStop :size="27" /></button>
        <div v-else class="voice-button pending"><AudioLines :size="27" /></div>
        <strong>{{ state === "recording" ? (autoConversation ? "自动聆听中" : "正在聆听") : state === "ready" ? "点击开始说话" : state === "idle" ? "建立语音会话" : state === "requesting_permission" ? "正在申请麦克风权限" : "正在处理" }}</strong>
      </div>

      <button v-if="state !== 'idle'" class="button secondary full" type="button" :disabled="state === 'closing'" @click="disconnect"><Link2Off :size="17" />结束会话</button>
      <p v-if="error" class="form-error">{{ error }}</p>
    </section>

    <section class="surface-section transcript-panel">
      <div class="section-header"><div><span class="eyebrow">转写</span><h2>会话内容</h2></div><Volume2 :size="18" class="text-muted" /></div>
      <div class="transcript-list">
        <div v-if="!transcripts.length" class="empty-state">暂无会话内容</div>
        <div v-for="item in transcripts" :key="item.id" :class="['transcript-row', item.kind]">
          <div class="transcript-icon"><UserRound v-if="item.kind === 'user'" :size="16" /><AudioLines v-else :size="16" /></div>
          <div><span>{{ item.kind === "user" ? "你" : item.kind === "assistant" ? "智能体" : "系统" }}</span><p>{{ item.text }}</p></div>
        </div>
      </div>
      <div class="realtime-text-input">
        <input v-model="textInput" placeholder="发送文本消息" :disabled="!connected" @keyup.enter="sendText" />
        <button class="icon-button primary-icon" type="button" title="发送" :disabled="!connected || !textInput.trim()" @click="sendText"><Send :size="17" /></button>
      </div>
    </section>
  </div>
</template>
